import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Lock, RefreshCw, LogOut, Mail, Trash2, Copy, Check, Users, ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';
import BrandLogo from '../components/BrandLogo';
import { toast } from '../hooks/use-toast';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = 'chainkart_admin_token';

const AdminPage = () => {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || '');
  const [authed, setAuthed] = useState(false);
  const [entries, setEntries] = useState([]);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [copiedId, setCopiedId] = useState(null);

  const fetchEntries = async (t = token) => {
    if (!t) return;
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/waitlist`, {
        headers: { 'X-Admin-Token': t },
      });
      setEntries(data.entries || []);
      setCount(data.count || 0);
      setAuthed(true);
      localStorage.setItem(TOKEN_KEY, t);
    } catch (err) {
      if (err?.response?.status === 401) {
        toast({ title: 'Invalid admin token' });
        setAuthed(false);
        localStorage.removeItem(TOKEN_KEY);
      } else {
        toast({ title: 'Failed to load entries' });
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) fetchEntries(token);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSubmit = (e) => {
    e.preventDefault();
    fetchEntries(token);
  };

  const handleLogout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken('');
    setAuthed(false);
    setEntries([]);
    setCount(0);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this signup?')) return;
    try {
      await axios.delete(`${API}/admin/waitlist/${id}`, {
        headers: { 'X-Admin-Token': token },
      });
      setEntries((prev) => prev.filter((e) => e.id !== id));
      setCount((c) => c - 1);
      toast({ title: 'Signup deleted' });
    } catch {
      toast({ title: 'Delete failed' });
    }
  };

  const handleCopyEmails = async () => {
    const emails = entries.map((e) => e.email).join(', ');
    try {
      await navigator.clipboard.writeText(emails);
      toast({ title: 'Emails copied to clipboard' });
    } catch {
      toast({ title: 'Copy failed' });
    }
  };

  const handleCopyOne = async (email, id) => {
    try {
      await navigator.clipboard.writeText(email);
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 1500);
    } catch {
      toast({ title: 'Copy failed' });
    }
  };

  const downloadCSV = () => {
    const header = 'email,source,email_sent,created_at\n';
    const rows = entries
      .map((e) => `"${e.email}","${e.source || ''}",${e.email_sent},${e.created_at}`)
      .join('\n');
    const blob = new Blob([header + rows], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `chainkart-waitlist-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // --- Auth screen ---
  if (!authed) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center px-5">
        <div className="w-full max-w-md">
          <div className="flex flex-col items-center gap-4 mb-8">
            <BrandLogo size={40} />
            <div className="text-[11px] tracking-[0.2em] uppercase text-neutral-500">
              Admin console
            </div>
          </div>
          <form
            onSubmit={handleSubmit}
            className="rounded-3xl border border-white/10 bg-[#0a0a0c] p-7"
          >
            <div className="h-12 w-12 rounded-2xl bg-[#39D353]/10 border border-[#39D353]/25 flex items-center justify-center">
              <Lock size={18} className="text-[#39D353]" />
            </div>
            <h1 className="mt-5 text-2xl font-bold tracking-tight">Enter admin token</h1>
            <p className="mt-2 text-sm text-neutral-400">
              Paste the token from your backend <code className="text-neutral-300">.env</code> to
              view all waitlist signups.
            </p>
            <input
              type="password"
              value={token}
              onChange={(e) => setToken(e.target.value)}
              placeholder="Admin token"
              className="mt-6 w-full h-12 rounded-full bg-white/5 border border-white/10 px-5 outline-none text-sm text-white placeholder:text-neutral-500 focus:border-[#39D353]/40"
            />
            <button
              type="submit"
              disabled={loading || !token}
              className="btn-neon mt-4 w-full h-12 rounded-full text-sm font-semibold disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {loading ? 'Verifying…' : 'Unlock'}
            </button>
            <Link
              to="/"
              className="mt-4 flex items-center justify-center gap-1.5 text-xs text-neutral-500 hover:text-white transition-colors"
            >
              <ArrowLeft size={13} /> Back to landing
            </Link>
          </form>
        </div>
      </div>
    );
  }

  // --- Dashboard ---
  return (
    <div className="min-h-screen bg-black text-white">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-black/85 backdrop-blur-md border-b border-white/5">
        <div className="max-w-6xl mx-auto px-5 md:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <BrandLogo size={36} />
            <span className="hidden sm:inline text-[11px] tracking-[0.2em] uppercase text-neutral-500 border-l border-white/10 pl-4">
              Admin
            </span>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => fetchEntries()}
              className="h-9 w-9 rounded-full bg-white/5 hover:bg-white/10 border border-white/10 flex items-center justify-center transition-colors"
              aria-label="Refresh"
              title="Refresh"
            >
              <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
            </button>
            <button
              onClick={handleLogout}
              className="inline-flex items-center gap-2 h-9 px-4 rounded-full bg-white/5 hover:bg-white/10 border border-white/10 text-xs font-medium transition-colors"
            >
              <LogOut size={13} /> Log out
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-5 md:px-8 py-10">
        {/* Stats row */}
        <div className="grid sm:grid-cols-3 gap-4">
          <div className="rounded-2xl border border-white/10 bg-[#0a0a0c] p-5">
            <div className="flex items-center gap-2 text-[#39D353] text-[11px] tracking-[0.2em] uppercase font-semibold">
              <Users size={14} /> Total signups
            </div>
            <div className="mt-3 text-3xl font-bold counter-num">{count}</div>
          </div>
          <div className="rounded-2xl border border-white/10 bg-[#0a0a0c] p-5">
            <div className="text-[11px] tracking-[0.2em] uppercase text-neutral-500 font-semibold">
              Confirmed emails sent
            </div>
            <div className="mt-3 text-3xl font-bold counter-num">
              {entries.filter((e) => e.email_sent).length}
            </div>
          </div>
          <div className="rounded-2xl border border-white/10 bg-[#0a0a0c] p-5">
            <div className="text-[11px] tracking-[0.2em] uppercase text-neutral-500 font-semibold">
              Latest signup
            </div>
            <div className="mt-3 text-sm text-neutral-300 truncate">
              {entries[0]?.email || '—'}
            </div>
          </div>
        </div>

        {/* Toolbar */}
        <div className="mt-8 flex items-center justify-between flex-wrap gap-3">
          <h2 className="text-lg font-semibold">All signups</h2>
          <div className="flex gap-2">
            <button
              onClick={handleCopyEmails}
              disabled={!entries.length}
              className="inline-flex items-center gap-2 h-9 px-4 rounded-full bg-white/5 hover:bg-white/10 border border-white/10 text-xs font-medium disabled:opacity-40 transition-colors"
            >
              <Copy size={13} /> Copy emails
            </button>
            <button
              onClick={downloadCSV}
              disabled={!entries.length}
              className="btn-neon inline-flex items-center gap-2 h-9 px-4 rounded-full text-xs font-semibold disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Export CSV
            </button>
          </div>
        </div>

        {/* Table */}
        <div className="mt-4 rounded-2xl border border-white/10 bg-[#0a0a0c] overflow-hidden">
          {entries.length === 0 ? (
            <div className="p-14 text-center">
              <Mail size={24} className="mx-auto text-neutral-500" />
              <div className="mt-3 text-sm text-neutral-400">
                {loading ? 'Loading…' : 'No signups yet. The list will appear here as people join.'}
              </div>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-[11px] tracking-[0.15em] uppercase text-neutral-500 border-b border-white/5">
                    <th className="py-3 px-5 font-semibold">#</th>
                    <th className="py-3 px-5 font-semibold">Email</th>
                    <th className="py-3 px-5 font-semibold hidden md:table-cell">Source</th>
                    <th className="py-3 px-5 font-semibold hidden md:table-cell">Email sent</th>
                    <th className="py-3 px-5 font-semibold">Joined</th>
                    <th className="py-3 px-5" />
                  </tr>
                </thead>
                <tbody>
                  {entries.map((e, i) => (
                    <tr
                      key={e.id}
                      className="border-b border-white/5 last:border-b-0 hover:bg-white/[0.02] transition-colors"
                    >
                      <td className="py-3 px-5 text-neutral-500">{i + 1}</td>
                      <td className="py-3 px-5 text-white font-medium">
                        <div className="flex items-center gap-2">
                          <span>{e.email}</span>
                          <button
                            onClick={() => handleCopyOne(e.email, e.id)}
                            className="text-neutral-500 hover:text-white transition-colors"
                            aria-label="Copy email"
                          >
                            {copiedId === e.id ? (
                              <Check size={13} className="text-[#39D353]" />
                            ) : (
                              <Copy size={13} />
                            )}
                          </button>
                        </div>
                      </td>
                      <td className="py-3 px-5 hidden md:table-cell">
                        <span className="inline-flex px-2 py-0.5 rounded-full text-[11px] bg-white/5 border border-white/10 text-neutral-300">
                          {e.source || 'landing'}
                        </span>
                      </td>
                      <td className="py-3 px-5 hidden md:table-cell">
                        {e.email_sent ? (
                          <span className="inline-flex items-center gap-1 text-[11px] text-[#39D353]">
                            <Check size={13} /> Sent
                          </span>
                        ) : (
                          <span className="text-[11px] text-neutral-500">Pending</span>
                        )}
                      </td>
                      <td className="py-3 px-5 text-neutral-400">
                        {new Date(e.created_at).toLocaleString()}
                      </td>
                      <td className="py-3 px-5 text-right">
                        <button
                          onClick={() => handleDelete(e.id)}
                          className="text-neutral-500 hover:text-red-400 transition-colors"
                          aria-label="Delete"
                        >
                          <Trash2 size={14} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default AdminPage;
